import React from "react";
import { Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import { LocationStatus, LOCATION_STATUS_STYLES } from "../../../types/LocationStatus";
import { LocationCardContext } from "./LocationCardContext";
import LocationCardImage from "./LocationCardImage";
import LocationCardContent from "./LocationCardContent";
import LocationCardHeader from "./LocationCardHeader";
import LocationCardActions from "./LocationCardActions";
import LocationCardFields from "./LocationCardFields";
import LocationCardField from "./LocationCardField";
import LocationCardSearchChip from "./LocationCardSearchChip";
import LocationCardPendingNotice from "./LocationCardPendingNotice";
import LocationCardDraftProgress from "./LocationCardDraftProgress";

interface LocationCardProps {
  derivedStatus: LocationStatus;
  children: React.ReactNode;
}

const CardRoot = styled(Box, {
  shouldForwardProp: (prop) => prop !== "ownerStatus",
})<{ ownerStatus: LocationStatus }>(({ ownerStatus, theme }) => ({
  background: "#fff",
  border: "1px solid #dde3e8",
  borderLeft: `4px solid ${LOCATION_STATUS_STYLES[ownerStatus].border}`,
  borderRadius: 4,
  display: "flex",
  // Mobile-first: image stacks above content
  flexDirection: "column",
  overflow: "hidden",
  boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
  transition: "box-shadow 0.15s",
  marginBottom: 12,
  ...(ownerStatus === "deactivated" && { opacity: 0.75 }),
  "&:hover": {
    boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
  },
  // SM+: image becomes left sidebar
  [theme.breakpoints.up("sm")]: {
    flexDirection: "row",
  },
}));

/**
 * Compound root component for a supplier location card.
 *
 * Provides a `LocationStatus` context to all sub-components so they can
 * style themselves without prop drilling. The card layout is mobile-first:
 * image stacks above content on small screens and moves to the left sidebar
 * at the `sm` breakpoint.
 *
 * Sub-components:
 * - `LocationCard.Image` — thumbnail with status ribbon
 * - `LocationCard.Content` — flex-column content wrapper
 * - `LocationCard.Header` — name, address, phone
 * - `LocationCard.Actions` — button slot (children only)
 * - `LocationCard.Fields` — responsive field grid
 * - `LocationCard.Field` — individual label + value cell
 * - `LocationCard.SearchStatusChip` — visible / not-visible pill
 * - `LocationCard.PendingNotice` — amber "in review" bar (Pending status)
 * - `LocationCard.DraftProgress` — completion % bar (Draft status)
 *
 * @example
 * ```tsx
 * <LocationCard derivedStatus="active">
 *   <LocationCard.Image fileName={location.listingImageFileName} />
 *   <LocationCard.Content>
 *     <LocationCard.Header
 *       name={location.locationName}
 *       address={`${location.addressLine1}, ${location.city}, ${location.state}`}
 *       phone={location.phoneNumber}
 *     />
 *     <LocationCard.Actions>
 *       <Button onClick={handleEdit}>Edit</Button>
 *       <Button onClick={handleDeactivate} color="error">Deactivate</Button>
 *     </LocationCard.Actions>
 *     <LocationCard.Fields>
 *       <LocationCard.Field label="Search Status">
 *         <LocationCard.SearchStatusChip isVisible={location.isVisible} />
 *       </LocationCard.Field>
 *       <LocationCard.Field label="Spaces">{location.managedSpaces}</LocationCard.Field>
 *       <LocationCard.Field label="Per Day">${location.pricePerDay}</LocationCard.Field>
 *     </LocationCard.Fields>
 *   </LocationCard.Content>
 * </LocationCard>
 * ```
 */
const LocationCard = ({ derivedStatus, children }: LocationCardProps) => (
  <LocationCardContext.Provider value={{ derivedStatus }}>
    <CardRoot ownerStatus={derivedStatus} aria-label={`${derivedStatus} location card`}>
      {children}
    </CardRoot>
  </LocationCardContext.Provider>
);

// Attach sub-components via dot-notation
LocationCard.Image          = LocationCardImage;
LocationCard.Content        = LocationCardContent;
LocationCard.Header         = LocationCardHeader;
LocationCard.Actions        = LocationCardActions;
LocationCard.Fields         = LocationCardFields;
LocationCard.Field          = LocationCardField;
LocationCard.SearchStatusChip  = LocationCardSearchChip;
LocationCard.PendingNotice  = LocationCardPendingNotice;
LocationCard.DraftProgress  = LocationCardDraftProgress;

export default LocationCard;

